import { useCallback, useEffect, useState } from 'react';
import {
  warehouseApi,
  type WarehouseResponse,
  type WmPickListResponse,
} from '@/api/warehouse.api';
import { PageHeader } from '@/components/layout/PageHeader';
import { ProductSelect } from '@/components/selects';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { usePermissions } from '@/hooks/usePermissions';
import { useProductOptions } from '@/hooks/useProductOptions';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';
export function WarehousePicksPage() {
  const { hasAny } = usePermissions();
  const canPick = hasAny('WAREHOUSE_PICK', 'WAREHOUSE_ADMIN');
  const { getLabel: getProductLabel } = useProductOptions();
  const [picks, setPicks] = useState<WmPickListResponse[]>([]);
  const [warehouses, setWarehouses] = useState<WarehouseResponse[]>([]);
  const [warehouseId, setWarehouseId] = useState('');
  const [productId, setProductId] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [pickedQty, setPickedQty] = useState('1');
  const [carrierRef, setCarrierRef] = useState('');

  const reload = useCallback(() => {
    warehouseApi.listOpenPicks().then(setPicks).catch((e) => showApiError(e, 'Failed to load picks'));
  }, []);

  useEffect(() => {
    reload();
    warehouseApi.listWarehouses().then(setWarehouses).catch(() => {});
  }, [reload]);

  const createPick = async () => {
    if (!warehouseId || !productId) return;
    try {
      await warehouseApi.createPick({
        warehouseId: Number(warehouseId),
        lines: [{ productId: Number(productId), quantity: Number(quantity) }],
      });
      notify.success('Pick list created');
      reload();
    } catch (e) {
      showApiError(e, 'Create pick failed');
    }
  };

  const confirmTask = async (taskId: number, qty: number) => {
    try {
      await warehouseApi.confirmPick(taskId, { pickedQuantity: qty });
      notify.success('Pick confirmed');
      reload();
    } catch (e) {
      showApiError(e, 'Confirm pick failed');
    }
  };

  const packAndDispatch = async (pickListId: number) => {
    try {
      const pack = await warehouseApi.createPack(pickListId);
      await warehouseApi.completePack(pack.id);
      const dispatch = await warehouseApi.createDispatch(pack.id);
      await warehouseApi.completeDispatch(dispatch.id, carrierRef.trim() || undefined);
      notify.success('Pack and dispatch completed');
      reload();
    } catch (e) {
      showApiError(e, 'Pack/dispatch failed');
    }
  };

  return (
    <div className="pb-safe">
      <PageHeader
        title="Pick / pack / dispatch"
        breadcrumbs={[
          { label: 'Org', to: '/org' },
          { label: 'Warehouse', to: '/warehouse' },
          { label: 'Picks' },
        ]}
        actions={<Button variant="secondary" onClick={reload}>Refresh</Button>}
      />

      <div className="card mb-6 p-4">
        <h2 className="mb-3 font-semibold">Create pick list</h2>
        {canPick ? (
          <>
            <div className="grid gap-3 sm:grid-cols-4">
              <Select
                label="Warehouse"
                value={warehouseId}
                onChange={(v) => setWarehouseId(v)}
                options={[
                  { value: '', label: 'Select…' },
                  ...warehouses.map((w) => ({ value: String(w.id), label: w.code })),
                ]}
              />
              <ProductSelect label="Product *" value={productId} onChange={setProductId} required />
              <Input label="Quantity" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
              <Input label="Picked qty (confirm)" value={pickedQty} onChange={(e) => setPickedQty(e.target.value)} />
            </div>
            <Input
              className="mt-3"
              label="Carrier ref (dispatch)"
              value={carrierRef}
              onChange={(e) => setCarrierRef(e.target.value)}
            />
            <Button className="mt-3" onClick={() => void createPick()}>
              Create pick
            </Button>
          </>
        ) : (
          <p className="text-sm text-slate-500">Pick permission required to create and confirm picks.</p>
        )}
      </div>

      {picks.length === 0 ? (
        <p className="text-sm text-slate-500">No open pick lists.</p>
      ) : (
        <ul className="space-y-4">
          {picks.map((p) => (
            <li key={p.id} className="card p-4">
              <p className="font-medium">
                {p.pickCode} · WH {p.warehouseId} · {p.statusCode}
              </p>
              <ul className="mt-2 space-y-2 text-sm">
                {p.tasks.map((t) => (
                  <li key={t.id} className="flex flex-wrap items-center gap-2">
                    Task {t.id}: {getProductLabel(t.productId, true)} · qty {t.quantity} ({t.statusCode})
                    {t.statusCode !== 'COMPLETED' && canPick && (
                      <Button size="sm" onClick={() => void confirmTask(t.id, Number(pickedQty) || t.quantity)}>
                        Confirm pick
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
              {canPick && (
                <Button className="mt-3" variant="secondary" onClick={() => void packAndDispatch(p.id)}>
                  Pack & dispatch
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
